import type { Shipment, ShipmentRouteStop } from '@/services/api/shipments';

/**
 * 4W per-stop outcomes — which drops count, and how many the driver failed,
 * Ops cancelled or the server skipped.
 *
 * Mirrors the server rule (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §9): counted drops
 * are the DROP_OFF stops that are not the return-to-origin stop.
 * Return-to-origin is the largest-sequence DROP_OFF lying ≤ 300 m (haversine)
 * from any PICKUP.
 *
 * The server marks that stop (`isReturnToOrigin`) on every evidence stop; the
 * web uses its flag whenever the route carries it. Only an older BE without
 * the flag falls back to a client reconstruction from what the shipment
 * already has: a DIRECT_4W shipment's origin is its first PICKUP and its
 * destination is its last DROP_OFF (direct4w-creation.service); other pickups
 * are matched on address. The chip (1E) and the Mark-completed warning (7C)
 * are informational, so a borderline miss degrades to "1/3" instead of "1/2" —
 * it never blocks an action.
 */

export const RETURN_TO_ORIGIN_RADIUS_M = 300;

export interface LatLng {
  lat: number;
  lng: number;
}

/** First PICKUP (origin) and last DROP_OFF (destination) coordinates. */
export interface RouteEndpoints {
  origin: LatLng | null;
  destination: LatLng | null;
}

const toLatLng = (
  lat: number | null | undefined,
  lng: number | null | undefined
): LatLng | null =>
  typeof lat === 'number' &&
  typeof lng === 'number' &&
  Number.isFinite(lat) &&
  Number.isFinite(lng)
    ? { lat, lng }
    : null;

export const routeEndpoints = (
  shipment: Pick<
    Shipment,
    'originLat' | 'originLong' | 'destinationLat' | 'destinationLong'
  > | null
): RouteEndpoints => ({
  origin: toLatLng(shipment?.originLat, shipment?.originLong),
  destination: toLatLng(shipment?.destinationLat, shipment?.destinationLong),
});

/** Great-circle distance in metres (same formula as the server's check). */
export const haversineMeters = (a: LatLng, b: LatLng): number => {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const normalizeAddress = (address: string | null | undefined) =>
  (address ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const bySequence = (a: ShipmentRouteStop, b: ShipmentRouteStop) =>
  a.sequence - b.sequence;

/**
 * The return-to-origin stop's id, or null when the route has none. The
 * server's `isReturnToOrigin` decides when any stop carries the flag.
 */
export const returnToOriginStopId = (
  stops: ShipmentRouteStop[],
  endpoints: RouteEndpoints
): string | null => {
  if (stops.some((s) => typeof s.isReturnToOrigin === 'boolean')) {
    return stops.find((s) => s.isReturnToOrigin === true)?.id ?? null;
  }
  const sorted = [...stops].sort(bySequence);
  const pickups = sorted.filter((s) => s.type === 'PICKUP');
  const lastDrop = sorted.filter((s) => s.type === 'DROP_OFF').pop();
  if (!lastDrop || pickups.length === 0) return null;

  // destination = last DROP_OFF, origin = first PICKUP.
  if (
    endpoints.origin &&
    endpoints.destination &&
    haversineMeters(endpoints.origin, endpoints.destination) <=
      RETURN_TO_ORIGIN_RADIUS_M
  ) {
    return lastDrop.id;
  }
  const dropAddress = normalizeAddress(lastDrop.address);
  if (
    dropAddress &&
    pickups.some((p) => normalizeAddress(p.address) === dropAddress)
  ) {
    return lastDrop.id;
  }
  return null;
};

/** DROP_OFF stops a driver can fail — every drop except return-to-origin. */
export const failableDrops = (
  stops: ShipmentRouteStop[],
  endpoints: RouteEndpoints
): ShipmentRouteStop[] => {
  const rto = returnToOriginStopId(stops, endpoints);
  return [...stops]
    .sort(bySequence)
    .filter((s) => s.type === 'DROP_OFF' && s.id !== rto);
};

/** A SKIPPED drop the server skipped because its single pickup failed. */
export const isPickupSkipped = (
  stop: Pick<ShipmentRouteStop, 'status' | 'skipReason'>
): boolean => stop.status === 'SKIPPED' && stop.skipReason === 'PICKUP_FAILED';

export interface DropFailureSummary {
  /** Counted drops with status FAILED (the "N" in "N/M drop gagal"). */
  failed: number;
  /** Counted drops Ops cancelled. */
  cancelled: number;
  /** Counted drops skipped because their pickup failed. */
  pickupSkipped: number;
  /** Counted drops (the "M" in "N/M drop gagal"). */
  failable: number;
}

export const dropFailureSummary = (
  stops: ShipmentRouteStop[] | null | undefined,
  endpoints: RouteEndpoints
): DropFailureSummary => {
  const drops = failableDrops(stops ?? [], endpoints);
  return {
    failed: drops.filter((s) => s.status === 'FAILED').length,
    cancelled: drops.filter((s) => s.status === 'CANCELLED').length,
    pickupSkipped: drops.filter(isPickupSkipped).length,
    failable: drops.length,
  };
};

/**
 * No counted drop can still be delivered: every one is FAILED, CANCELLED or
 * skipped because its pickup failed — the server settles the shipment FAILED
 * at end-route (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §9, eng review D9).
 */
export const shipmentSettlesFailed = (summary: DropFailureSummary): boolean =>
  summary.failable > 0 &&
  summary.failed + summary.cancelled + summary.pickupSkipped ===
    summary.failable;

/**
 * Reason codes that mean "the recipient asked us to come back later" — a
 * drop (EC5009) or a pickup (EB3007). Still a FAILED stop in Fase 1; Ops
 * reschedules, so the web marks it apart from a terminal failure.
 */
export const RESCHEDULE_REASON_CODES: readonly string[] = ['EC5009', 'EB3007'];

export const isRescheduleReason = (code: string | null | undefined): boolean =>
  !!code && RESCHEDULE_REASON_CODES.includes(code);
